import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { APIActivitySettingsDebugOperationsItem } from "@/lib/generated/model";
import type { ActivitySettings } from "@/lib/api";

export function ActivityDebugSettings({ value, onChange, products }: {
  value: ActivitySettings;
  onChange: (value: ActivitySettings) => void;
  products: { value: string; label: string }[];
}) {
  const { t } = useTranslation();
  const patch = (next: Partial<ActivitySettings>) => onChange({ ...value, ...next });
  return <fieldset className="grid gap-3 border-t border-line pt-3">
    <legend className="px-1 text-sm font-medium">{t("activity.debugCapture")}</legend>
    <label className="flex items-center gap-2 text-sm"><Checkbox checked={value.debug_enabled ?? false} onCheckedChange={(debug_enabled) => patch({ debug_enabled })} />{t("activity.debugEnabled")}</label>
    <p className="text-xs text-muted">{t("activity.debugIntersection")}</p>
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="grid gap-1 text-sm">{t("activity.debugScope")}
        <Select value={value.debug_scope ?? "inherit"} onValueChange={(debug_scope) => patch({ debug_scope: debug_scope as ActivitySettings["debug_scope"], debug_product_ids: debug_scope === "selected" ? value.debug_product_ids : [] })}
          options={[{ value: "inherit", label: t("activity.inheritScope") }, { value: "all", label: t("activity.allProducts") }, { value: "selected", label: t("activity.selectedProducts") }]} />
      </label>
      <label className="grid gap-1 text-sm">{t("activity.debugRetention")}
        <Input type="number" required min={1} max={24} value={value.debug_retention_hours ?? 24} onChange={(event) => patch({ debug_retention_hours: Number(event.target.value) })} />
      </label>
    </div>
    {value.debug_scope === "selected" && <fieldset className="flex flex-wrap gap-3"><legend className="mb-2 text-sm">{t("activity.productsToCapture")}</legend>
      {products.map((product) => <label key={product.value} className="flex items-center gap-2 text-sm"><Checkbox checked={value.debug_product_ids?.includes(product.value) ?? false} onCheckedChange={(checked) => patch({ debug_product_ids: checked ? [...(value.debug_product_ids ?? []), product.value] : value.debug_product_ids?.filter((id) => id !== product.value) })} />{product.label}</label>)}
    </fieldset>}
    <fieldset className="flex flex-wrap gap-3"><legend className="mb-2 text-sm">{t("activity.debugOperations")}</legend>
      {Object.values(APIActivitySettingsDebugOperationsItem).map((operation) => <label key={operation} className="flex items-center gap-2 font-mono text-xs"><Checkbox checked={value.debug_operations?.includes(operation) ?? false} onCheckedChange={(checked) => patch({ debug_operations: checked ? [...(value.debug_operations ?? []), operation] : value.debug_operations?.filter((item) => item !== operation) })} />{operation}</label>)}
    </fieldset>
    <div className="grid gap-3 sm:grid-cols-2">
      {(["debug_starts_at", "debug_ends_at"] as const).map((field) => <label key={field} className="grid gap-1 text-sm">{t(field === "debug_starts_at" ? "activity.debugStarts" : "activity.debugEnds")}
        <Input type="datetime-local" value={localTime(value[field])} onChange={(event) => patch({ [field]: event.target.value ? new Date(event.target.value).toISOString() : null })} />
      </label>)}
    </div>
  </fieldset>;
}

function localTime(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
